import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { MicroService } from "@/types";
import {
  generateRoutesFromMicroservices,
  getAuthenticatedRoutes,
  getPublicRoutes,
} from "@/config/routeGenerator";
import { request } from "@/services/api";
import { resolveSecureMicroserviceUrl } from "@/utils/resolveSecureMicroserviceUrl";
import {
  appendFallbackMicroservicesFromPermissions,
  userCanAccessMicroservice,
} from "@/utils/microserviceAccess";
// import { serviceService } from "@/services/serviceService";

interface ServiceContextType {
  services: MicroService[];
  activeServices: MicroService[];
  isLoading: boolean;
  error: string | null;
  refreshServices: () => Promise<void>;
  getServiceByName: (_name: string) => MicroService | undefined;
  getServiceById: (_id: string) => MicroService | undefined;
  // 动态路由相关
  getDynamicRoutes: () => any[];
  getAuthenticatedRoutes: () => string[];
  getPublicRoutes: () => string[];
}

const ServiceContext = createContext<ServiceContextType | undefined>(undefined);

interface ServiceProviderProps {
  children: ReactNode;
}

const normalizeUrl = (url?: string): string => {
  if (!url) {
    return "";
  }

  const isHttpsPage =
    typeof window !== "undefined" && window.location.protocol === "https:";

  if (/^https?:\/\//i.test(url)) {
    if (isHttpsPage && /^http:\/\//i.test(url)) {
      return url.replace(/^http:\/\//i, "https://");
    }
    return url;
  }

  return `${isHttpsPage ? "https" : "http"}://${url}`;
};

const formatDisplayUrl = (url?: string): string => {
  if (!url) {
    return "";
  }

  try {
    const normalized = normalizeUrl(url);
    const parsed = new URL(normalized);
    const hostname = parsed.hostname;
    const port = parsed.port ? `:${parsed.port}` : "";
    const path = parsed.pathname === "/" ? "" : parsed.pathname;
    return `${hostname}${port}${path}`;
  } catch (error) {
    return url.replace(/^https?:\/\//, "");
  }
};

function isMsadminName(name: string | undefined): boolean {
  return String(name ?? "")
    .trim()
    .toLowerCase() === "msadmin";
}

/**
 * API 原始列表 + 权限兜底（如 DB 无 msorg）+ 按用户过滤。
 * 已持有 mslogin_token 但尚未写入 mslogin_user（竞态）或解析失败时，不得返回含 msadmin 的全量列表，否则侧栏齿轮会对无权限用户闪现。
 */
function buildVisibleMicroservicesForUser(base: MicroService[]): MicroService[] {
  const userDataStr = localStorage.getItem("mslogin_user");
  const hasLoginToken = Boolean(localStorage.getItem("mslogin_token"));

  if (!userDataStr) {
    if (hasLoginToken) {
      return base.filter((s) => !isMsadminName(s.name));
    }
    return base;
  }
  try {
    const userData = JSON.parse(userDataStr);
    const userPermissions = userData.permissions || [];
    const merged = appendFallbackMicroservicesFromPermissions(
      base,
      userPermissions,
      userData,
    );
    return merged.filter((service) =>
      userCanAccessMicroservice(service, userData, userPermissions),
    );
  } catch {
    if (hasLoginToken) {
      return base.filter((s) => !isMsadminName(s.name));
    }
    return base;
  }
}

export const ServiceProvider: React.FC<ServiceProviderProps> = ({
  children,
}) => {
  const [services, setServices] = useState<MicroService[]>([]);
  const [activeServices, setActiveServices] = useState<MicroService[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** 未按用户过滤的 API 列表，供登录后补 fallback（如 DB 无 msorg）时重建菜单 */
  const apiServicesBaseRef = useRef<MicroService[]>([]);

  // 初始化服务列表
  useEffect(() => {
    refreshServices();

    // 完全禁用定时刷新，避免不必要的重新渲染
    // const interval = setInterval(() => {
    //   console.log("[ServiceContext] 定时刷新服务列表");
    //   refreshServices();
    // }, 30000); // 30秒

    // 清理定时器
    // return () => clearInterval(interval);
  }, []);

  // 根据用户权限过滤服务列表
  const filterServicesByPermissions = useCallback(() => {
    const userDataStr = localStorage.getItem("mslogin_user");

    // 如果没有用户数据，不清空服务列表，等待用户数据加载
    if (!userDataStr) {
      return;
    }

    const base = apiServicesBaseRef.current;
    if (base.length === 0) {
      refreshServices();
      return;
    }

    const filteredServices = buildVisibleMicroservicesForUser(base);

    setServices(filteredServices);
    setActiveServices(
      filteredServices.filter(
        (service) => service.status === "active" || service.status === "online"
      )
    );
  }, []);

  // 监听用户权限变化，重新过滤服务列表
  useEffect(() => {
    const handleStorageChange = () => {
      // 只重新过滤服务列表，不重新获取数据
      filterServicesByPermissions();
    };

    // 监听localStorage变化
    window.addEventListener("storage", handleStorageChange);

    // 监听自定义事件（用于同窗口内的权限变化）
    window.addEventListener("userPermissionsChanged", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("userPermissionsChanged", handleStorageChange);
    };
  }, [filterServicesByPermissions]);

  // 刷新服务列表
  const refreshServices = async () => {
    try {
      // 只在首次加载时设置 loading 状态，避免不必要的重新渲染
      if (services.length === 0) {
        setIsLoading(true);
      }
      setError(null);

      // 使用配置好的request工具获取服务列表
      // 不传递分页参数，获取所有服务
      const result = await request.get("/api/microservices", {
        params: {
          all: true, // 获取所有服务，不分页
        },
      });

      if (result.success && result.data) {
        // 将后端数据转换为前端 MicroService 格式
        const apiServices: MicroService[] = result.data.map(
          (service: any, index: number) => {
            const normalizedFront = normalizeUrl(service.url);
            const secureFront =
              resolveSecureMicroserviceUrl(service.name, {
                url: normalizedFront,
                routePath: service.routePath,
                name: service.name,
              }) || normalizedFront;
            return {
            id: service.key || (index + 1).toString(),
            name: service.name,
            displayName: service.displayName,
            description: service.description || "",
            url: secureFront, // 前端访问URL（HTTPS / 同域）
            displayUrl: formatDisplayUrl(service.url),
            backendUrl: service.backendUrl
              ? normalizeUrl(service.backendUrl)
              : undefined, // 后端API URL
            serviceType:
              service.serviceType ??
              service.service_type ??
              (service.isSystemService ? 0 : 1),
            status: service.status as
              | "active"
              | "inactive"
              | "error"
              | "loading"
              | "online"
              | "offline",
            version: service.version,
            icon: service.icon || "api",
            color: service.color || "#1890ff",
            permissions: service.permissions || [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            routePath: service.routePath,
            isMenuItem: service.isMenuItem,
            menuOrder: service.menuOrder,
            requiresAuth: service.requiresAuth,
            isExternal: service.external,
            serviceUrl: service.backendUrl
              ? normalizeUrl(service.backendUrl)
              : secureFront,
            isSystemService: service.isSystemService, // 添加系统服务标识
            healthStatus: service.healthStatus,
            lastHealthCheck: service.lastHealthCheck,
          };
          }
        );

        apiServicesBaseRef.current = apiServices;

        // 根据用户权限过滤；无登录态时展示 API 全量（与原先一致）
        let filteredServices = buildVisibleMicroservicesForUser(apiServices);

        // 只在数据真正变化时才更新状态，避免不必要的重新渲染
        const newActiveServices = filteredServices.filter(
          (service) =>
            service.status === "active" || service.status === "online"
        );

        // 对于 msadmin 服务，保持对象引用稳定，避免 iframe 重新加载
        // 如果新的服务列表中有 msadmin，且旧的列表中也存在，保持 msadmin 服务对象的引用不变
        let finalServices = filteredServices;
        const oldMsAdminService = services.find((s) => isMsadminName(s.name));
        const newMsAdminService = filteredServices.find((s) =>
          isMsadminName(s.name),
        );

        if (oldMsAdminService && newMsAdminService) {
          // 检查 msadmin 服务的关键字段是否变化
          const msAdminKeyChanged =
            oldMsAdminService.name !== newMsAdminService.name ||
            oldMsAdminService.url !== newMsAdminService.url ||
            oldMsAdminService.id !== newMsAdminService.id;

          if (!msAdminKeyChanged) {
            // msadmin 服务的关键字段没变，保持对象引用不变
            finalServices = filteredServices.map((service) => {
              if (isMsadminName(service.name)) {
                return oldMsAdminService; // 返回旧的对象引用
              }
              return service;
            });
          }
        }

        // 比较数据是否真的发生了变化
        // 使用更准确的比较方式：比较服务 ID 列表和关键字段
        const oldServiceIds = new Set(services.map((s) => s.id));
        const newServiceIds = new Set(finalServices.map((s) => s.id));
        const idsChanged =
          oldServiceIds.size !== newServiceIds.size ||
          [...newServiceIds].some((id) => !oldServiceIds.has(id)) ||
          [...oldServiceIds].some((id) => !newServiceIds.has(id));

        // 如果 ID 列表相同，检查关键字段是否变化
        let servicesChanged = idsChanged;
        if (!idsChanged) {
          // ID 列表相同，检查每个服务的关键字段是否变化
          const oldServiceMap = new Map(services.map((s) => [s.id, s]));
          servicesChanged = finalServices.some((newService) => {
            const oldService = oldServiceMap.get(newService.id);
            if (!oldService) return true;
            // 比较关键字段
            return (
              oldService.name !== newService.name ||
              oldService.url !== newService.url ||
              oldService.displayName !== newService.displayName ||
              oldService.isMenuItem !== newService.isMenuItem ||
              oldService.menuOrder !== newService.menuOrder
            );
          });
        }

        const activeServicesChanged =
          JSON.stringify(activeServices) !== JSON.stringify(newActiveServices);

        if (servicesChanged) {
          // 强制创建新数组，确保 React 检测到变化
          setServices([...finalServices]);
        }
        if (activeServicesChanged) {
          setActiveServices(newActiveServices);
        }
      } else {
        throw new Error(result.message || "获取服务列表失败");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "获取服务列表失败";
      setError(errorMessage);

      // 如果 API 调用失败，使用空数组
      setServices([]);
      setActiveServices([]);
    } finally {
      setIsLoading(false);
    }
  };

  // 根据名称获取服务
  const getServiceByName = (name: string): MicroService | undefined => {
    return services.find((service) => service.name === name);
  };

  // 根据ID获取服务
  const getServiceById = (id: string): MicroService | undefined => {
    return services.find((service) => service.id === id);
  };

  // 获取动态路由
  const getDynamicRoutes = () => {
    return generateRoutesFromMicroservices(services);
  };

  // 获取需要认证的路由
  const getAuthenticatedRoutesList = () => {
    return getAuthenticatedRoutes(services);
  };

  // 获取公开路由
  const getPublicRoutesList = () => {
    return getPublicRoutes();
  };

  const contextValue: ServiceContextType = {
    services,
    activeServices,
    isLoading,
    error,
    refreshServices,
    getServiceByName,
    getServiceById,
    getDynamicRoutes,
    getAuthenticatedRoutes: getAuthenticatedRoutesList,
    getPublicRoutes: getPublicRoutesList,
  };

  return (
    <ServiceContext.Provider value={contextValue}>
      {children}
    </ServiceContext.Provider>
  );
};

export const useService = (): ServiceContextType => {
  const context = useContext(ServiceContext);
  if (context === undefined) {
    throw new Error("useService must be used within a ServiceProvider");
  }
  return context;
};
